import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

import { OpsContextService } from '../../../core/ops/ops-context.service';
import { GlossaryPageService } from '../../../core/glossary/glossary-page.service';
import {
  GlossaryPanelComponent,
  GlossaryPanelData,
} from './glossary-panel/glossary-panel';
import { IconComponent, IconName } from '../../../shared/icon/icon';

/** One entry in the Ops sidebar. */
interface OpsNavItem {
  label: string;
  path: string;
  icon: IconName;
}

/**
 * The Ops workspace shell — a persistent sidebar wrapping every `/ops/*` screen.
 *
 * The nav is **function-first** (DOC-52 / DOC-51 §2): one workspace organised by
 * what you are doing, with the product surfaced as context on the screen itself.
 * That is why `/ops/applications` (every KS and DF application, one product per
 * tab) is the nav item and there is no per-product one — a per-product sidebar
 * would rebuild the silos the unified workspace exists to remove. `/ops/kickstart`
 * redirects to the Kickstart tab (FLOW-346).
 *
 * DOC-51 §2 adds a **Collections** function as a peer of these items. It is
 * deliberately absent until FLOW-122 builds the surface: an item pointing at a
 * route that does not exist is exactly the dead control N7 forbids. Adding it is
 * one line in {@link navItems} once the route lands.
 *
 * No item is tier-gated. Every `/ops` route requires only an Authorised Ops user
 * (any of Analyst / Senior Ops / Credit Committee), so there is nothing here to
 * suppress per tier — authority differences bite at the action, not the queue.
 */
@Component({
  selector: 'app-ops-layout',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, IconComponent],
  templateUrl: './ops-layout.html',
  styleUrl: './ops-layout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpsLayoutComponent {
  private readonly opsContext = inject(OpsContextService);
  private readonly injector = inject(Injector);
  private readonly dialog = inject(MatDialog);
  private readonly glossaryPage = inject(GlossaryPageService);

  /** *What these terms mean* — the whole glossary, this page's terms first. */
  protected openGlossary(): void {
    this.dialog.open<GlossaryPanelComponent, GlossaryPanelData>(GlossaryPanelComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: { pageKeys: this.glossaryPage.pageKeys() },
    });
  }

  /** The authority the user is acting under, shown against the workspace title. */
  protected readonly tierLabel = this.opsContext.opsTierLabel;

  /**
   * Scroll the newly-active nav item into view.
   *
   * Only the narrow (<=900px) layout can need this: there the nav is a
   * horizontally scrolling strip whose scroll position is independent of the
   * route, so landing on `/ops/debtors` at phone width left "Debtors" outside
   * the visible window with the strip still parked at `scrollLeft: 0` — the
   * user saw Dashboard and no indication of where they were. On desktop all
   * seven items fit the column, so `'nearest'` makes this a no-op rather than
   * something a media query has to switch off.
   *
   * `block: 'nearest'` matters: without it the call also scrolls the *page*
   * to the top of the nav on every navigation.
   *
   * Deferred to `afterNextRender` because `isActiveChange` fires during change
   * detection, before the strip has been laid out on a first paint — measuring
   * then yields a zero-width scroll container and the call silently does nothing.
   */
  protected revealActive(anchor: HTMLElement): void {
    afterNextRender(() => anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' }), {
      injector: this.injector,
    });
  }

  protected readonly navItems: readonly OpsNavItem[] = [
    { label: 'Dashboard', path: '/ops/dashboard', icon: 'chart' },
    { label: 'Pre-applications', path: '/ops/pre-applications', icon: 'inbox' },
    { label: 'Applications', path: '/ops/applications', icon: 'document' },
    { label: 'Debtors', path: '/ops/debtors', icon: 'users' },
    { label: 'Invoices', path: '/ops/df/invoices', icon: 'wallet' },
    { label: 'Facilities', path: '/ops/df/facilities', icon: 'briefcase' },
    { label: 'Breaching SLA', path: '/ops/breaching', icon: 'alert-triangle' },
    // FLOW-304: `§B6.2`'s financing-words and known-lenders lists — the
    // engine's loan-money lookup, kept by Ops. A function of the workspace,
    // not a product queue, so it sits with the other cross-product items.
    { label: 'Lookup lists', path: '/ops/lookup-lists', icon: 'book' },
  ];
}
